import { Controller } from "@hotwired/stimulus"
import Shepherd from "shepherd.js"
import { offset } from '@floating-ui/dom'
import { AttendanceTour } from "../../utils/attendance_tour"

// Connects to data-controller="tours--attendance-setup"
//
// Continues the attendance tour inside Settings › Attendance after the report
// page hands off. It only starts when the hand-off flag is set, then shows the
// steps for the current page: the categories index (whose `phase` reflects how
// far setup has progressed), the Rock RMS metrics picker, or the category form.
//
// Intermediate pages close the tour without clearing the flag so it resumes on
// the next page load; explicit cancels and the final phase clear it.
export default class extends Controller {
  static values = {
    page: { type: String, default: 'index' },
    phase: { type: String, default: 'category' },
    rockRms: { type: Boolean, default: false },
    chms: { type: String, default: '' },
    labelsCount: { type: Number, default: 0 },
    reportUrl: { type: String, default: '/topics/attendance' },
    metricsUrl: { type: String, default: '' },
    newCategoryUrl: { type: String, default: '' }
  }

  connect() {
    if (!AttendanceTour.setupActive()) return

    this.build()
    this.onCancel = () => AttendanceTour.endSetup()
    this.tour.once('cancel', this.onCancel)
    this.tour.start()
  }

  disconnect() {
    if (!this.tour?.isActive()) return

    // Navigating between setup pages is not a cancel; keep the flag so the
    // tour picks up on the next page.
    this.tour.off('cancel', this.onCancel)
    this.tour.cancel()
  }

  build() {
    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        // Override default z-index to ensure it is behind side_modal
        classes: 'box box--card z-9998',
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 6,
        modalOverlayOpeningRadius: 12,
        floatingUIOptions: { middleware: [offset(10)] }
      },
      useModalOverlay: true
    })

    switch (this.pageValue) {
      case 'metrics':
        this.addMetricsSteps()
        break
      case 'category':
        this.addCategorySteps()
        break
      default:
        this.addIndexSteps()
    }
  }

  // Settings › Attendance index

  addIndexSteps() {
    switch (this.phaseValue) {
      case 'metrics':
        this.addSelectMetricsStep()
        break
      case 'waiting':
        this.addWaitingStep()
        break
      case 'assign':
        this.addAssignStep()
        break
      case 'done':
        this.addDoneStep()
        break
      default:
        this.addCreateCategorySteps()
    }
  }

  addSelectMetricsStep() {
    this.tour.addStep({
      id: 'select-metrics',
      title: "Select your Rock RMS events",
      text: `MortarStone pulls Weekly Metrics from Rock RMS. Pick the ones that should count toward
        attendance; each becomes an event you can place in a category.`,
      attachTo: { element: '#attendance_import_info', on: 'bottom' },
      buttons: [
        this.skipButton,
        this.visitButton('Select Events', this.metricsUrlValue)
      ]
    })
  }

  addWaitingStep() {
    this.tour.addStep({
      id: 'waiting',
      title: "Waiting on your next import",
      text: this.waitingText,
      attachTo: { element: '#attendance_import_info', on: 'bottom' },
      buttons: [this.finishButton]
    })
  }

  get waitingText() {
    const source = this.chmsValue || 'your church management system'

    if (this.rockRmsValue) {
      return `Your selected metrics will arrive as events on the next import from ${source}.
        Once they're here, come back to group them into categories.
        <br><br>Thanks for using MortarStone!`
    }

    return `No events have been imported from ${source} yet. They'll show up after the next import;
      come back then to group them into categories.
      <br><br>Thanks for using MortarStone!`
  }

  addCreateCategorySteps() {
    this.tour.addStep({
      id: 'events',
      title: "Your events",
      text: `${this.labelsSummary} Now group them into categories that match your ministries.`,
      attachTo: { element: '#attendance_import_info', on: 'bottom' },
      buttons: [this.skipButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'add-category',
      title: "Add a category",
      text: `Create your first category, something like <em>Adult Worship</em> or <em>Youth Ministry</em>.
        You can add more later.`,
      attachTo: { element: '#add_category_button', on: 'bottom' },
      modalOverlayOpeningRadius: 8,
      buttons: [
        this.backButton,
        this.visitButton('Add Category', this.newCategoryUrlValue)
      ]
    })
  }

  get labelsSummary() {
    const count = this.labelsCountValue
    const noun = count === 1 ? 'event has' : 'events have'
    const source = this.chmsValue ? ` from ${this.chmsValue}` : ''

    return `${count} ${noun} been imported${source}.`
  }

  addAssignStep() {
    this.tour.addStep({
      id: 'assign',
      title: "Assign events to your category",
      text: `Your category doesn't have any events yet. Open it, check the events that belong in it,
        and save.`,
      attachTo: { element: '#attendance_categories', on: 'top' },
      buttons: [this.skipButton, this.gotItButton]
    })
  }

  addDoneStep() {
    this.tour.addStep({
      id: 'done',
      title: "You're all set",
      text: `Your reports will total attendance by these categories. Add or edit categories here any time.
        <br><br>Head back to Attendance to see your numbers.`,
      attachTo: { element: '#attendance_categories', on: 'top' },
      buttons: [
        {
          action: this.finishToReport.bind(this),
          text: 'Back to Attendance',
          classes: 'btn btn-solid'
        }
      ]
    })
  }

  // Rock RMS metrics picker

  addMetricsSteps() {
    this.tour.addStep({
      id: 'choose-metrics',
      title: "Choose events",
      text: `Check each metric you want counted toward attendance. Search by name or page through the list;
        your selections carry across pages.`,
      attachTo: { element: '#rock_rms_metrics_box', on: 'top' },
      buttons: [this.skipButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'save-metrics',
      title: "Save your selection",
      text: `Click <strong>Update</strong> when you're done. The tour picks back up on the next page.`,
      attachTo: { element: '#rock_rms_metrics_actions', on: 'bottom' },
      modalOverlayOpeningRadius: 8,
      buttons: [this.backButton, this.gotItButton]
    })
  }

  // Category form (new and edit)

  addCategorySteps() {
    this.tour.addStep({
      id: 'name',
      title: "Name the category",
      text: `Use a name that matches how you talk about your ministries. Reports will label
        attendance with it.`,
      attachTo: { element: '#headcount_category_name', on: 'bottom' },
      modalOverlayOpeningRadius: 6,
      buttons: [this.skipButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'default',
      title: "Default category",
      text: `Default categories are pre-selected when someone opens the attendance report.
        Mark your main worship services as default.`,
      attachTo: { element: '#headcount_category_default_field', on: 'bottom' },
      modalOverlayOpeningRadius: 6,
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'labels',
      title: "Choose events",
      text: `Check every event that belongs in this category. Search by name or page through the list;
        your selections carry across pages.`,
      attachTo: { element: '#headcount_labels_box', on: 'top' },
      buttons: [this.backButton, this.nextButton]
    })

    this.tour.addStep({
      id: 'save-category',
      title: "Save the category",
      text: `Click <strong>${this.saveButtonLabel}</strong> when you're done. The tour picks back up on the next page.`,
      attachTo: { element: '#headcount_category_submit', on: 'bottom' },
      modalOverlayOpeningRadius: 8,
      buttons: [this.backButton, this.gotItButton]
    })
  }

  get saveButtonLabel() {
    return document.querySelector('#headcount_category_submit')?.value || 'Save'
  }

  // Navigation

  visitButton(text, url) {
    return {
      text,
      classes: 'btn btn-solid',
      action: () => this.visit(url)
    }
  }

  visit(url) {
    // disconnect() will close the tour without clearing the hand-off flag
    Turbo.visit(url)
  }

  finishToReport() {
    AttendanceTour.endSetup()
    this.tour.complete()
    Turbo.visit(`${this.reportUrlValue}?tour=attendance`)
  }

  finish() {
    AttendanceTour.endSetup()
    this.tour.complete()
  }

  // Buttons

  get skipButton() {
    return { action: this.tour.cancel, text: 'Skip', classes: 'btn btn-link-slate' }
  }

  get backButton() {
    return { action: this.tour.back, text: 'Back', classes: 'btn btn-link-slate' }
  }

  get nextButton() {
    return { action: this.tour.next, text: 'Next', classes: 'btn btn-solid' }
  }

  get finishButton() {
    return { action: this.finish.bind(this), text: 'Finish', classes: 'btn btn-solid' }
  }

  // Closes the tour but leaves the hand-off flag so it resumes after the
  // user submits the form on this page.
  get gotItButton() {
    return { action: this.tour.complete, text: 'Got it', classes: 'btn btn-solid' }
  }
}
